<template>
  <v-dialog v-model="open" max-width="1200" scrollable>
    <v-card v-if="snapshot">
      <v-card-title class="d-flex align-center">
        <span class="text-h6">
          Snapshot #{{ snapshot.id }}
        </span>
        <v-chip class="ml-3" small :color="triggerColor(snapshot.trigger_type)" dark>
          {{ snapshot.trigger_type }}
          <span v-if="snapshot.trigger_type !== 'baseline'">
            · palier {{ snapshot.threshold_gb }} Go
          </span>
        </v-chip>
        <span class="ml-3 text--secondary">
          {{ formatDate(snapshot.created_at) }}
        </span>
        <v-spacer />
        <v-btn icon @click="open = false">
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="pt-0">
        <span class="mr-4">
          RAM used: <b>{{ (snapshot.mem_used_mb / 1024).toFixed(2) }} Go</b>
          / {{ (snapshot.mem_total_mb / 1024).toFixed(2) }} Go
          (avail {{ (snapshot.mem_available_mb / 1024).toFixed(2) }} Go)
        </span>
        <span v-if="snapshot.swap_total_mb > 0" class="mr-4">
          Swap used: {{ (snapshot.swap_used_mb / 1024).toFixed(2) }} Go
        </span>
        <span>{{ snapshot.process_count }} process</span>
      </v-card-subtitle>

      <v-tabs v-model="tab" class="px-4">
        <v-tab>Processes</v-tab>
        <v-tab>Diff</v-tab>
        <v-tab v-if="snapshot.meminfo_extra">/proc/meminfo</v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text style="height: 65vh">
        <v-tabs-items v-model="tab">
          <!-- ─────── Processes ─────── -->
          <v-tab-item>
            <v-data-table
              :items="snapshot.processes || []"
              :headers="processHeaders"
              :items-per-page="-1"
              dense
              hide-default-footer
              :sort-by.sync="processSortBy"
              :sort-desc.sync="processSortDesc"
              class="mt-2"
            >
              <template #item.rss_mb="{ item }">
                <b>{{ formatMb(item.rss_mb) }}</b>
              </template>
              <template #item.name="{ item }">
                {{ item.name }}
              </template>
              <template #item.cmdline="{ item }">
                <span :title="item.cmdline" class="cmdline-cell">
                  {{ item.cmdline || item.name }}
                </span>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ─────── Diff ─────── -->
          <v-tab-item>
            <div class="d-flex align-center mt-2 mb-3">
              <span class="mr-2">Comparer à&nbsp;:</span>
              <v-chip small color="primary" outlined class="mr-3">
                snapshot précédent · même trigger ({{ snapshot.trigger_type }})
              </v-chip>
              <span
                v-if="!snapshot.previous_id_same_trigger"
                class="text--secondary caption"
              >
                Aucun snapshot précédent du même trigger.
              </span>
              <span
                v-else-if="loadingPrev"
                class="text--secondary caption"
              >
                Chargement…
              </span>
              <span
                v-else-if="previousSnapshot"
                class="text--secondary caption"
              >
                #{{ previousSnapshot.id }} —
                {{ formatDate(previousSnapshot.created_at) }}
              </span>
            </div>

            <div v-if="diffSections" class="mt-2">
              <DiffSection
                title="Apparus"
                color="success"
                :items="diffSections.appeared"
              />
              <DiffSection
                title="Grandi"
                color="warning"
                :items="diffSections.grew"
              />
              <DiffSection
                title="Réduits"
                color="info"
                :items="diffSections.shrunk"
              />
              <DiffSection
                title="Disparus"
                color="grey"
                :items="diffSections.disappeared"
              />
            </div>
            <div v-else class="text-center text--secondary py-6">
              Aucun diff disponible.
            </div>
          </v-tab-item>

          <!-- ─────── meminfo extra ─────── -->
          <v-tab-item v-if="snapshot.meminfo_extra">
            <v-simple-table dense class="mt-2">
              <thead>
                <tr>
                  <th>Clé</th>
                  <th class="text-right">Valeur (kB)</th>
                  <th class="text-right">Valeur (Mo)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(value, key) in snapshot.meminfo_extra" :key="key">
                  <td><code>{{ key }}</code></td>
                  <td class="text-right">{{ value.toLocaleString("fr-FR") }}</td>
                  <td class="text-right">{{ (value / 1024).toFixed(1) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "@axios";
import { mdiClose } from "@mdi/js";

const aggregateByKey = (processes) => {
  const out = {};
  for (const p of processes || []) {
    const key = p.process_key || "unknown";
    if (!out[key]) {
      out[key] = {
        process_key: key,
        rss_mb: 0,
        instance_count: 0,
        name: p.name,
        cmdline: p.cmdline,
        uid: p.uid,
      };
    }
    out[key].rss_mb += p.rss_mb || 0;
    out[key].instance_count += 1;
  }
  return out;
};

const DiffSection = {
  name: "DiffSection",
  props: {
    title: { type: String, required: true },
    color: { type: String, default: "primary" },
    items: { type: Array, default: () => [] },
  },
  computed: {
    sorted() {
      return [...this.items].sort(
        (a, b) => Math.abs(b.delta_mb) - Math.abs(a.delta_mb)
      );
    },
  },
  render(h) {
    if (this.sorted.length === 0) return null;
    return h(
      "div",
      { staticClass: "diff-section mb-4" },
      [
        h(
          "div",
          { staticClass: "d-flex align-center mb-1" },
          [
            h(
              "v-chip",
              { props: { color: this.color, dark: true, small: true } },
              this.title
            ),
            h(
              "span",
              { staticClass: "ml-2 text--secondary caption" },
              `${this.sorted.length} entrée${this.sorted.length > 1 ? "s" : ""}`
            ),
          ]
        ),
        h(
          "v-simple-table",
          { props: { dense: true } },
          [
            h("thead", {}, [
              h("tr", {}, [
                h("th", "Process"),
                h("th", { staticClass: "text-right" }, "Prev RSS"),
                h("th", { staticClass: "text-right" }, "Curr RSS"),
                h("th", { staticClass: "text-right" }, "Δ"),
                h("th", { staticClass: "text-right" }, "Instances"),
              ]),
            ]),
            h(
              "tbody",
              {},
              this.sorted.map((item) =>
                h("tr", { key: item.process_key }, [
                  h(
                    "td",
                    [
                      h("b", item.name || "—"),
                      h(
                        "span",
                        { staticClass: "text--secondary caption ml-2" },
                        `uid ${item.uid}`
                      ),
                      h(
                        "div",
                        {
                          staticClass: "caption text--secondary",
                          attrs: { title: item.cmdline },
                          style:
                            "max-width: 600px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;",
                        },
                        item.cmdline || ""
                      ),
                    ]
                  ),
                  h(
                    "td",
                    { staticClass: "text-right" },
                    item.prev_rss != null ? `${item.prev_rss} Mo` : "—"
                  ),
                  h(
                    "td",
                    { staticClass: "text-right" },
                    item.curr_rss != null ? `${item.curr_rss} Mo` : "—"
                  ),
                  h(
                    "td",
                    {
                      staticClass: "text-right",
                      style:
                        item.delta_mb > 0
                          ? "color:#ef6c00;font-weight:600"
                          : item.delta_mb < 0
                          ? "color:#43a047"
                          : "",
                    },
                    item.delta_mb > 0
                      ? `+${item.delta_mb} Mo`
                      : item.delta_mb < 0
                      ? `${item.delta_mb} Mo`
                      : "0"
                  ),
                  h(
                    "td",
                    { staticClass: "text-right" },
                    `${item.prev_instances || 0} → ${item.curr_instances || 0}`
                  ),
                ])
              )
            ),
          ]
        ),
      ]
    );
  },
};

export default {
  name: "SnapshotDetailDialog",
  components: { DiffSection },
  props: {
    value: { type: Boolean, default: false },
    snapshotId: { type: Number, default: null },
  },
  data() {
    return {
      snapshot: null,
      previousSnapshot: null,
      loading: false,
      loadingPrev: false,
      tab: 0,
      processSortBy: "rss_mb",
      processSortDesc: true,
      processHeaders: [
        { text: "RSS", value: "rss_mb", align: "end" },
        { text: "PID", value: "pid", align: "end" },
        { text: "Name", value: "name" },
        { text: "UID", value: "uid", align: "end" },
        { text: "Cmdline", value: "cmdline", sortable: false },
      ],
      icons: { mdiClose },
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    diffSections() {
      if (!this.snapshot || !this.previousSnapshot) return null;
      const a = aggregateByKey(this.previousSnapshot.processes);
      const b = aggregateByKey(this.snapshot.processes);
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      const appeared = [];
      const grew = [];
      const shrunk = [];
      const disappeared = [];
      for (const k of keys) {
        const prev = a[k];
        const curr = b[k];
        const prevRss = prev ? prev.rss_mb : 0;
        const currRss = curr ? curr.rss_mb : 0;
        const delta = currRss - prevRss;
        const sample = curr || prev;
        const row = {
          process_key: k,
          name: sample.name,
          cmdline: sample.cmdline,
          uid: sample.uid,
          prev_rss: prev ? prevRss : null,
          curr_rss: curr ? currRss : null,
          delta_mb: delta,
          prev_instances: prev ? prev.instance_count : 0,
          curr_instances: curr ? curr.instance_count : 0,
        };
        if (!prev) appeared.push(row);
        else if (!curr) disappeared.push(row);
        else if (delta > 0) grew.push(row);
        else if (delta < 0) shrunk.push(row);
      }
      return { appeared, grew, shrunk, disappeared };
    },
  },
  watch: {
    snapshotId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchAll(id);
      },
    },
  },
  methods: {
    async fetchAll(id) {
      this.snapshot = null;
      this.previousSnapshot = null;
      this.tab = 0;
      this.loading = true;
      try {
        const { data } = await axios.get(`/system/ram-snapshots/${id}`);
        this.snapshot = data;
        if (data.previous_id_same_trigger) {
          this.loadingPrev = true;
          try {
            const { data: prev } = await axios.get(
              `/system/ram-snapshots/${data.previous_id_same_trigger}`
            );
            this.previousSnapshot = prev;
          } finally {
            this.loadingPrev = false;
          }
        }
      } catch (e) {
        this.snapshot = null;
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      return new Date(d).toLocaleString("fr-FR");
    },
    formatMb(v) {
      if (v == null) return "—";
      if (v >= 1024) return `${(v / 1024).toFixed(2)} Go`;
      return `${v} Mo`;
    },
    triggerColor(t) {
      if (t === "threshold") return "orange";
      if (t === "swap") return "purple";
      return "grey";
    },
  },
};
</script>

<style scoped>
.cmdline-cell {
  display: inline-block;
  max-width: 600px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
</style>
